import type {
  HumanProvenanceRequest,
  HumanRootRef,
} from "../../api/humanProvenance";
import type { DagViewerNode } from "./types";
import { parseHumanNodeId } from "./humanAdapter";
import {
  canonicalHumanRecordKinds,
  humanFiltersForRequest,
  humanRecordKindsForRequest,
  type HumanFilterState,
} from "./humanViewState";

export interface HumanWorkspaceCut {
  effectiveAsOf: string;
  recordedAsOf: string;
  snapshotToken: string;
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function decisionRef(value: unknown): HumanRootRef | null {
  const item = objectValue(value);
  return item?.entity_kind === "decision" && typeof item.id === "string"
    ? { entity_kind: "decision", id: item.id }
    : null;
}

/** Resolve only typed, already-visible node identities into human query roots. */
export function humanRootForNode(node: DagViewerNode | null): HumanRootRef | null {
  if (!node) return null;
  const human = parseHumanNodeId(node.id);
  if (human?.entity_kind === "decision") return human;
  if (human?.entity_kind === "journal_entry") return human;
  if (human?.entity_kind === "decision_evidence") return human;
  if (human?.entity_kind === "decision_status_event") {
    return decisionRef(node.metadata.human_decision_ref);
  }
  if (node.metadata.human_restriction_marker === "restricted_evidence_omitted") {
    return decisionRef(node.metadata.human_decision_ref);
  }
  if (node.semanticRole === "artifact_version") {
    const canonicalId = node.metadata.canonical_entity_kind === "artifact_version"
      && typeof node.metadata.canonical_id === "string"
      ? node.metadata.canonical_id
      : node.id.startsWith("artifact_version:") ? node.id.slice("artifact_version:".length) : null;
    return canonicalId ? { entity_kind: "artifact_version", id: canonicalId } : null;
  }
  return null;
}

export function humanRootForSelection(
  selectedId: string | null,
  selectedNode: DagViewerNode | null,
): HumanRootRef | null {
  const parsed = parseHumanNodeId(selectedId);
  if (parsed?.entity_kind === "decision" || parsed?.entity_kind === "journal_entry"
    || parsed?.entity_kind === "decision_evidence") return parsed;
  if (selectedId?.startsWith("artifact_version:") && selectedId.split(":").length === 2) {
    return { entity_kind: "artifact_version", id: selectedId.slice("artifact_version:".length) };
  }
  if (selectedId?.startsWith("decision:") && selectedId.endsWith(":restricted-evidence")) {
    const id = selectedId.slice("decision:".length, -":restricted-evidence".length);
    return id ? { entity_kind: "decision", id } : null;
  }
  return humanRootForNode(selectedNode);
}

export function buildHumanProvenanceRequest({
  projectId,
  selectedNode,
  selectedId,
  searchQuery,
  filters,
  cut,
}: {
  projectId: string;
  selectedNode: DagViewerNode | null;
  selectedId: string | null;
  searchQuery: string;
  filters: HumanFilterState;
  cut: HumanWorkspaceCut;
}): HumanProvenanceRequest {
  const recordKinds = humanRecordKindsForRequest(filters);
  const exact = {
    project_id: projectId,
    as_of: cut.effectiveAsOf,
    recorded_as_of: cut.recordedAsOf,
    snapshot_token: cut.snapshotToken,
    record_kinds: recordKinds,
  };
  const query = searchQuery.trim();
  if (query) {
    return {
      ...exact,
      projection: "search",
      root_ref: null,
      query,
      filters: humanFiltersForRequest(filters),
    };
  }
  const root = humanRootForSelection(selectedId, selectedNode);
  if (root && root.entity_kind !== "artifact_version") {
    return {
      ...exact,
      record_kinds: canonicalHumanRecordKinds([...recordKinds, root.entity_kind]),
      projection: "focus",
      root_ref: root,
      query: null,
      filters: {},
    };
  }
  if (root?.entity_kind === "artifact_version") {
    return {
      ...exact,
      projection: "related",
      root_ref: root,
      query: null,
      filters: humanFiltersForRequest(filters),
    };
  }
  return {
    ...exact,
    projection: "snapshot",
    root_ref: null,
    query: null,
    filters: humanFiltersForRequest(filters),
  };
}

export function buildHumanFacetBaseRequest({
  projectId,
  searchQuery,
  filters,
  cut,
}: {
  projectId: string;
  searchQuery: string;
  filters: HumanFilterState;
  cut: HumanWorkspaceCut;
}): HumanProvenanceRequest {
  const query = searchQuery.trim();
  return {
    project_id: projectId,
    projection: query ? "search" : "snapshot",
    root_ref: null,
    record_kinds: humanRecordKindsForRequest(filters),
    query: query || null,
    filters: humanFiltersForRequest(filters),
    as_of: cut.effectiveAsOf,
    recorded_as_of: cut.recordedAsOf,
    snapshot_token: cut.snapshotToken,
  };
}
